import { useEnsAddress as useWagmiEnsAddress } from 'wagmi'

import { isPotentialENSName } from '@src/utils/ensUtils'

interface UseEnsAddressProps {
    name: string
    chainId?: number
}

/**
 * Hook to resolve an ENS name to an Ethereum address using wagmi
 * Supports all dot-separated strings as potential ENS names (not just .eth domains)
 * Only attempts resolution when the name contains a dot
 *
 * @param name The ENS name to resolve (e.g., "example.eth", "user.xyz", etc.)
 * @param chainId The chain ID to use for resolution (defaults to 1 - Ethereum Mainnet)
 * @returns Object with data (resolved address), isLoading, and error states
 */
export function useEnsAddress({ name, chainId = 1 }: UseEnsAddressProps) {
    // Only pass the name to wagmi if it's a potential ENS name (contains a dot)
    const shouldResolve = isPotentialENSName(name)

    const result = useWagmiEnsAddress({
        name: shouldResolve ? name : undefined,
        chainId,
    })

    return {
        ...result,
        // If we're not even attempting to resolve, make sure isLoading is false
        isLoading: shouldResolve ? result.isLoading : false,
    }
}

export default useEnsAddress
